import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles.css';
import './styles-theme.css';
import './styles-team-register.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './i18n'; // Import i18n configuration
import './utils/axiosConfig'; // Set up global axios interceptors

// Polyfill for crypto.randomUUID if not available
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  crypto.randomUUID = function() {
    // Generate a UUID v4 using crypto.getRandomValues (more widely supported)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] % 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };
}

// Filter out browser extension console messages and WebSocket connection errors
const originalLog = console.log;
const originalInfo = console.info;
const originalWarn = console.warn;
const originalError = console.error;

// Override console.error to filter webpack-dev-server Event objects
console.error = (...args) => {
  // Check if any argument is an Event object from webpack-dev-server
  const hasWebpackEvent = args.some(arg => 
    arg && typeof arg === 'object' && 
    (arg.type === 'error' || arg.type === 'close') &&
    (arg.target && arg.target.constructor && arg.target.constructor.name === 'WebSocket') &&
    (String(arg).includes('[webpack-dev-server]') || String(arg).includes('webpack-dev-server'))
  );
  
  // Check if message contains webpack-dev-server WebSocket errors
  const message = args.map(arg => String(arg)).join(' ').toLowerCase();
  const isWebpackWebSocketError = 
    (message.includes('webpack-dev-server') && message.includes('websocket')) ||
    (message.includes('webpack-dev-server') && message.includes('event')) ||
    (message.includes('installhook') && message.includes('webpack-dev-server')) ||
    (message.includes('[webpack-dev-server]') && (message.includes('event') || message.includes('error'))) ||
    (message.includes('websocket connection to') && message.includes('ws://localhost:3000/ws'));
  
  if (hasWebpackEvent || isWebpackWebSocketError) {
    return; // Suppress the error
  }
  
  originalError.apply(console, args);
};

const shouldFilter = (args) => {
  const message = args.join(' ').toLowerCase();
  return message.includes('chext') || 
         message.includes('chext_driver') || 
         message.includes('chext_loader') ||
         message.includes('initialized driver') ||
         message.includes('initialized chextloader') ||
         (message.includes('unload event listeners') && message.includes('deprecated')) ||
         // Filter WebSocket connection errors that occur during reconnection/cleanup
         message.includes('websocket is closed before the connection is established') ||
         (message.includes('websocket connection to') && message.includes('failed')) ||
         // Filter WebSocket errors for port 3000 (React dev server - Socket.io will fallback to polling)
         (message.includes('websocket') && message.includes(':3000')) ||
         // Filter webpack-dev-server HMR WebSocket errors
         (message.includes('websocket connection to') && message.includes('ws://localhost:3000/ws')) ||
         (message.includes('webpack-dev-server') && message.includes('websocket')) ||
         (message.includes('react refresh') && message.includes('websocket')) ||
         (message.includes('installhook') && message.includes('webpack-dev-server')) ||
         (message.includes('[webpack-dev-server]') && message.includes('event'));
};

const shouldFilterError = (args) => {
  const message = args.join(' ').toLowerCase();
  return shouldFilter(args) ||
         // Filter WebSocket errors that are side effects of cleanup
         (message.includes('websocket') && message.includes('closed') && message.includes('connection')) ||
         // Filter WebSocketClient.js errors (Socket.io internal connection attempts)
         (message.includes('websocketclient') && message.includes('failed')) ||
         // Filter the specific "WebSocket is closed before the connection is established" error
         (message.includes('websocket connection to') && message.includes('failed') && message.includes('closed before')) ||
         // Filter "WebSocket is closed before the connection is established" (exact match)
         (message.includes('websocket is closed before the connection is established')) ||
         // Filter Socket.io WebSocket connection errors
         (message.includes('socket.io') && message.includes('websocket') && message.includes('failed')) ||
         // Filter any WebSocket errors related to socket.io transport fallback
         (message.includes('websocket') && message.includes('socket.io') && (message.includes('failed') || message.includes('closed'))) ||
         // Filter WebSocket connection errors to port 3000 (React dev server - Socket.io fallback)
         (message.includes('websocket connection to') && message.includes(':3000')) ||
         // Filter WebSocket errors to app.playlivepro.com:3000 (incorrect port during upgrade)
         (message.includes('websocket connection to') && message.includes('app.playlivepro.com') && message.includes(':3000')) ||
         // Filter webpack-dev-server HMR WebSocket errors
         (message.includes('websocket connection to') && message.includes('ws://localhost:3000/ws')) ||
         (message.includes('webpack-dev-server') && message.includes('websocket')) ||
         (message.includes('react refresh') && message.includes('websocket')) ||
         (message.includes('websocketclient') && message.includes('webpack-dev-server')) ||
         (message.includes('installhook') && message.includes('webpack-dev-server')) ||
         (message.includes('[webpack-dev-server]') && (message.includes('event') || message.includes('error'))) ||
         // Filter ERR_CONNECTION_ABORTED errors (expected during connection retries)
         (message.includes('err_connection_aborted')) ||
         // Filter ERR_SSL_PROTOCOL_ERROR (expected when backend is HTTP but frontend tries HTTPS)
         (message.includes('err_ssl_protocol_error')) ||
         // Filter "Failed to load resource" errors for expected connection failures
         (message.includes('failed to load resource') && (message.includes('err_connection_aborted') || message.includes('err_ssl_protocol_error')));
};

if (process.env.NODE_ENV === 'development') {
  console.log = (...args) => {
    if (!shouldFilter(args)) {
      originalLog.apply(console, args);
    }
  };

  console.info = (...args) => {
    if (!shouldFilter(args)) {
      originalInfo.apply(console, args);
    }
  };

  console.warn = (...args) => {
    if (!shouldFilter(args)) {
      originalWarn.apply(console, args);
    }
  };

  console.error = (...args) => {
    if (!shouldFilterError(args)) {
      originalError.apply(console, args);
    }
  };
} else {
  // In production, only filter WebSocket errors to reduce noise
  console.error = (...args) => {
    if (!shouldFilterError(args)) {
      originalError.apply(console, args);
    }
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
);

// Suppress WebSocket errors from window error events (browser-native errors)
const originalWindowError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  const errorMessage = String(message || '').toLowerCase();
  // Suppress WebSocket connection errors that occur during cleanup/reconnection
  // These are expected when socket.io tries websocket and falls back to polling
  if (errorMessage.includes('websocket') && 
      (errorMessage.includes('closed before') || 
       errorMessage.includes('connection failed') ||
       errorMessage.includes('failed: websocket is closed') ||
       errorMessage.includes(':3000') || // Filter port 3000 WebSocket errors
       errorMessage.includes('ws://localhost:3000/ws') || // Filter webpack-dev-server HMR
       (errorMessage.includes('app.playlivepro.com') && errorMessage.includes(':3000')) ||
       (errorMessage.includes('socket.io') && errorMessage.includes('failed')) ||
       (errorMessage.includes('socket.io') && errorMessage.includes('websocket') && errorMessage.includes('closed')) ||
       (errorMessage.includes('webpack-dev-server') && errorMessage.includes('websocket')) ||
       (errorMessage.includes('react refresh') && errorMessage.includes('websocket')))) {
    return true; // Suppress the error
  }
  // Suppress network errors that are expected during connection retries
  if (errorMessage.includes('failed to load resource') && 
      (errorMessage.includes('err_connection_aborted') || 
       errorMessage.includes('err_ssl_protocol_error') ||
       errorMessage.includes('net::err_connection_aborted') ||
       errorMessage.includes('net::err_ssl_protocol_error'))) {
    return true; // Suppress the error
  }
  // Call original error handler if not a WebSocket error
  if (originalWindowError) {
    return originalWindowError(message, source, lineno, colno, error);
  }
  return false;
};

// Suppress unhandled promise rejections related to WebSocket
window.addEventListener('unhandledrejection', (event) => {
  const reason = String(event.reason || '').toLowerCase();
  if (reason.includes('websocket') && 
      (reason.includes('closed before') || 
       reason.includes('connection failed') ||
       reason.includes('websocket is closed before the connection is established') ||
       reason.includes(':3000') || // Filter port 3000 WebSocket errors
       reason.includes('ws://localhost:3000/ws') || // Filter webpack-dev-server HMR
       (reason.includes('app.playlivepro.com') && reason.includes(':3000')) ||
       (reason.includes('socket.io') && reason.includes('failed')) ||
       (reason.includes('webpack-dev-server') && reason.includes('websocket')) ||
       (reason.includes('react refresh') && reason.includes('websocket')))) {
    event.preventDefault(); // Suppress the error
  }
  // Suppress network errors that are expected during connection retries
  if (reason.includes('err_connection_aborted') || 
      reason.includes('err_ssl_protocol_error') ||
      reason.includes('network error') ||
      (reason.includes('axioserror') && (reason.includes('connection') || reason.includes('timeout')))) {
    event.preventDefault(); // Suppress the error
  }
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
